import gql from 'graphql-tag';
import {Apollo} from 'apollo-angular';

/** The tracks and ffprobe streams of an item's source folder, enough to find its video duration. */
export const FOLDER_SOURCE_PROBE = gql`query FolderSourceProbe($id: ID!) {
  folder(id: $id) {
    id
    files {
      id
      name
      tracks {
        index
        enabled
        track_type
      }
      ffprobe
    }
  }
}`;

interface ProbeFile {
  name?: string;
  tracks?: { index: number; enabled: boolean; track_type: string }[] | null;
  ffprobe?: string[] | null;
}

/**
 * Duration in seconds of the source's video track -- the first enabled video track of the folder's
 * files, which is the one the workflow selects -- from its ffprobe stream (`duration`, or the
 * stream's `tags.DURATION` as hh:mm:ss.fff). null when there is no video track or no usable probe.
 */
export function sourceVideoDurationSeconds(files: ProbeFile[] | null | undefined): number | null {
  for (const file of files ?? []) {
    const video = (file.tracks ?? []).find(t => t.enabled && t.track_type === 'video');
    if (!video) {
      continue;
    }
    const streams = (file.ffprobe ?? []).map(raw => {
      try {
        return typeof raw === 'string' ? JSON.parse(raw) : raw;
      } catch {
        return null;
      }
    });
    const stream = streams.find(st => st?.index === video.index) ?? streams.find(st => st?.codec_type === 'video');
    return streamDuration(stream);
  }
  return null;
}

function streamDuration(stream: any): number | null {
  const plain = parseFloat(stream?.duration);
  if (isFinite(plain) && plain > 0) {
    return plain;
  }
  const tag = stream?.tags?.DURATION ?? stream?.tags?.duration;
  const m = typeof tag === 'string' ? /^(\d+):(\d{2}):(\d{2}(?:\.\d+)?)$/.exec(tag) : null;
  return m ? parseInt(m[1], 10) * 3600 + parseInt(m[2], 10) * 60 + parseFloat(m[3]) : null;
}

/**
 * Source video durations per item source (`input_brefix`), loaded on first ask. Read on every change
 * detection, as the PO forms do: null while loading, without a video track or without a usable probe.
 */
export class SourceDurations {
  private readonly known = new Map<string, number | null>();

  constructor(private readonly apollo: Apollo) {
  }

  of(brefix: string | null | undefined): number | null {
    if (!brefix) {
      return null;
    }
    if (!this.known.has(brefix)) {
      this.known.set(brefix, null);
      this.apollo.query<any>({query: FOLDER_SOURCE_PROBE, variables: {id: `s3://${brefix}`}}).subscribe({
        next: r => this.known.set(brefix, sourceVideoDurationSeconds(r.data?.folder?.files)),
        error: err => console.error(`source probe of ${brefix} failed`, err),
      });
    }
    return this.known.get(brefix) ?? null;
  }
}
